import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import {
    Alert,
    Box,
    Button,
    Chip,
    CircularProgress,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
} from '@mui/material';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Link as RouterLink } from 'react-router-dom';
import { useUser } from '../contexts/UserContext';
import Heartbeat, { isHeartbeatAlive } from '../components/Heartbeat';
import {
    DEFAULT_HEARTBEAT_MAX_AGE_MS,
    HEARTBEAT_MAX_AGE_MS_BY_SOURCE_TYPE,
    getHeartbeatMaxAgeMs,
} from '../utils/heartbeatPolicy';

export { isHeartbeatAlive };
export {
    DEFAULT_HEARTBEAT_MAX_AGE_MS,
    HEARTBEAT_MAX_AGE_MS_BY_SOURCE_TYPE,
    getHeartbeatMaxAgeMs,
};

export function groupHeartbeatsByType(heartbeats) {
    const groups = new Map();

    heartbeats.forEach((sourceHeartbeat) => {
        const type = sourceHeartbeat.type || 'Unknown';
        if (!groups.has(type)) groups.set(type, []);
        groups.get(type).push(sourceHeartbeat);
    });

    return Array.from(groups.entries())
        .sort(([typeA], [typeB]) => typeA.localeCompare(typeB))
        .map(([type, items]) => ({
            type,
            heartbeats: items.slice().sort((a, b) =>
                (a.sensor || '').localeCompare(b.sensor || '')
            ),
        }));
}

function HeartbeatGroup({ group, now }) {
    const [expanded, setExpanded] = useState(false);
    const activeCount = group.heartbeats.filter(sourceHeartbeat =>
        isHeartbeatAlive(
            sourceHeartbeat.heartbeat?.timestamp,
            getHeartbeatMaxAgeMs(sourceHeartbeat.type),
            now
        )
    ).length;
    const allActive = activeCount === group.heartbeats.length;

    return (
        <>
            <TableRow>
                <TableCell
                    component="th"
                    scope="colgroup"
                    colSpan={6}
                    sx={{ backgroundColor: 'action.hover' }}
                >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Button
                            onClick={() => setExpanded(value => !value)}
                            startIcon={expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                            aria-expanded={expanded}
                            sx={{ fontWeight: 'bold', textTransform: 'none' }}
                        >
                            {group.type} ({group.heartbeats.length})
                        </Button>
                        <Chip
                            label={`${activeCount}/${group.heartbeats.length} active`}
                            color={allActive ? 'success' : 'error'}
                            size="small"
                        />
                    </Box>
                </TableCell>
            </TableRow>
            {expanded && group.heartbeats.map((sourceHeartbeat, index) => (
                <Heartbeat
                    key={`${sourceHeartbeat.sensor}-${index}`}
                    sensor={sourceHeartbeat.sensor}
                    heartbeat={sourceHeartbeat.heartbeat}
                    maxAgeMs={getHeartbeatMaxAgeMs(sourceHeartbeat.type)}
                    now={now}
                />
            ))}
        </>
    );
}

function Heartbeats() {
    const { user } = useUser();
    const [heartbeats, setHeartbeats] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [now, setNow] = useState(Date.now());
    const canView = Boolean(user.token) && user.hasAnyRole(['jedimaster', 'heartbeats']);

    const loadHeartbeats = useCallback(async () => {
        if (!canView) return;

        setLoading(true);
        setError('');
        try {
            const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/sources/heartbeats`, {
                headers: { Authorization: `Bearer ${user.token}` },
            });
            setHeartbeats(Array.isArray(response.data) ? response.data : []);
            setNow(Date.now());
        } catch (requestError) {
            setError(requestError.response?.data?.message || 'Unable to load heartbeat data');
        } finally {
            setLoading(false);
        }
    }, [canView, user.token]);

    useEffect(() => {
        loadHeartbeats();
    }, [loadHeartbeats]);

    useEffect(() => {
        const timer = setInterval(() => {
            loadHeartbeats();
            setNow(Date.now());
        }, 30 * 1000);
        return () => clearInterval(timer);
    }, []);

    if (!canView) {
        return <Alert severity="error" sx={{ margin: 3 }}>Only jedimaster users can view heartbeats.</Alert>;
    }

    return (
        <Box sx={{ padding: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 2 }}>
                <div>
                    <Typography variant="h4" component="h1">Heartbeats</Typography>
                </div>
                <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button component={RouterLink} to="/map" variant="outlined">
                        Show on map
                    </Button>
                    <Button variant="outlined" onClick={loadHeartbeats} disabled={loading}>Refresh</Button>
                </Box>
            </Box>

            {error && <Alert severity="error" sx={{ marginBottom: 2 }}>{error}</Alert>}

            {loading && heartbeats.length === 0 ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', padding: 4 }}>
                    <CircularProgress aria-label="Loading heartbeats" />
                </Box>
            ) : (
                <TableContainer component={Paper}>
                    <Table aria-label="Source heartbeats">
                        <TableHead>
                            <TableRow>
                                <TableCell>Source</TableCell>
                                <TableCell>Latest heartbeat</TableCell>
                                <TableCell>Firmware</TableCell>
                                <TableCell>Status</TableCell>
                                <TableCell>Last seen</TableCell>
                                <TableCell>Details</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {groupHeartbeatsByType(heartbeats).map((group) => (
                                <HeartbeatGroup key={group.type} group={group} now={now} />
                            ))}
                            {!loading && heartbeats.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={6} align="center">No heartbeat data found.</TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}
        </Box>
    );
}

export default Heartbeats;
